import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, Close } from 'blaze-ui/icons'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import { useCreditUsage } from 'hooks/useCreditUsage'
import Styles from '../../VisualStylesForExistingUsersModal.module.scss'

const VIDEO_PREVIEW_URL =
  'https://res.cloudinary.com/almanac/video/upload/v1768515232/blaze_assets/content_preferences/Smile-landscape_c2b1lu.mp4'

interface MobileAnimatedImagesStepProps {
  onOptIn: (optIn: boolean) => void
  onBack: () => void
  onClose: () => void
}

export const MobileAnimatedImagesStep = observer(({ onOptIn, onBack, onClose }: MobileAnimatedImagesStepProps) => {
  const { usageCosts } = useCreditUsage()
  const animatedImagesCost = usageCosts?.video_credits_cost || 0

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-animated-images-step-mobile',
    })
  }, [])

  const handleOptIn = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-animated-images-step-mobile',
      animated_images_opt_in: true,
    })
    onOptIn(true)
  }

  const handleSkip = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-animated-images-step-mobile',
      animated_images_opt_in: false,
    })
    onOptIn(false)
  }

  return (
    <div className={Styles.mobileAnimatedImagesStep}>
      <div className={Styles.mobileAnimatedImagesStepHeader}>
        <Button size="sm" variant="tertiary" onPress={onBack}>
          <ArrowLeft size={20} color="var(--light-100)" />
        </Button>
        <Button size="sm" variant="tertiary" onPress={onClose}>
          <Close size={20} color="var(--light-100)" />
        </Button>
      </div>

      <div className={Styles.mobileAnimatedImagesContent}>
        <div className={Styles.mobileAnimatedImagesHeader}>
          <Heading level={1} color="var(--light-100)">
            Transform your photos into engaging AI videos
          </Heading>
          <Text color="var(--light-60)" variant="primary">
            Blaze automatically transforms your Brand Kit photos into professional videos using AI
          </Text>
        </div>

        <div className={Styles.mobileVideoContainer}>
          <video src={VIDEO_PREVIEW_URL} autoPlay muted loop playsInline className={Styles.mobileHorizontalVideo} />
        </div>

        <div className={Styles.mobileFeaturesList}>
          <div className={Styles.mobileFeatureItem}>
            <Text variant="primary" fontWeight="500" color="var(--light-100)">
              📹 More content, zero effort
            </Text>
            <Text variant="secondary" color="var(--light-60)">
              Blaze picks your best photos and turns them into videos automatically
            </Text>
          </div>

          <div className={Styles.mobileFeatureItem}>
            <Text variant="primary" fontWeight="500" color="var(--light-100)">
              💫 Scroll-stopping quality
            </Text>
            <Text variant="secondary" color="var(--light-60)">
              Professional camera movements, effects, and music in every 8-second video
            </Text>
          </div>

          <div className={Styles.mobileFeatureItem}>
            <Text variant="primary" fontWeight="500" color="var(--light-100)">
              ✅ Always ready to post
            </Text>
            <Text variant="secondary" color="var(--light-60)">
              Videos appear in your calendar—review, edit, or publish instantly
            </Text>
          </div>
        </div>
      </div>

      <div className={Styles.mobileAnimatedImagesFooter}>
        <Button variant="primary" size="lg" onPress={handleSkip} className={Styles.mobileFullWidthButton}>
          Continue without AI videos
        </Button>
        <Button variant="secondary" size="lg" onPress={handleOptIn} className={Styles.mobileFullWidthButton}>
          Yes, turn my images into AI videos
        </Button>
        <div className={Styles.mobileCreditsCost}>
          <Text variant="metadata" color="var(--light-60)">
            AI videos use {animatedImagesCost} credits each
          </Text>
        </div>
      </div>
    </div>
  )
})
